import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Check, Loader2, Search } from 'lucide-react';
import { createPortal } from 'react-dom';

import { useAuth } from '@/shared/hooks/useAuth';
import { useOrganizationSwitch } from '@/shared/hooks/useOrganizationSwitch';
import { getOrgInitials } from '@/shared/utils/initials';

import styles from './SidebarOrgSwitcher.module.css';

function getBrandFilterAllowedIds(
  features: ReadonlyArray<{ key: string; enabled: boolean; details: Record<string, unknown> }> | null | undefined,
): string[] | null {
  if (!features) return null;
  const brandFilter = features.find((f) => f.key === 'BRAND_FILTER');
  if (!brandFilter?.enabled) return null;

  const allowed = brandFilter.details?.allowed_workos_org_ids;
  if (!Array.isArray(allowed) || allowed.length === 0) return null;
  const ids = allowed.filter((v): v is string => typeof v === 'string' && v.length > 0);
  return ids.length > 0 ? ids : null;
}

/**
 * Org avatar tints. Brand greens and warm neutrals only.
 *
 * This was a seven-colour rotation including --burgundy-600 (#BF1D16), which
 * is the sale/urgency red — an organization hashing to it rendered a bright red
 * "TH" badge that read as an alert. It also included --purple-* and
 * --highlight-600; the latter now aliases --brand-600 and silently duplicated
 * index 0, so two orgs could get identical avatars.
 */
const ORG_AVATAR_COLORS = [
  { bg: 'var(--brand-700)', text: 'var(--static-white)' },
  { bg: 'var(--purple-700)', text: 'var(--static-white)' },
  { bg: 'var(--brand-300)', text: 'var(--brand-700)' },
  { bg: 'var(--gray-600)', text: 'var(--static-white)' },
  { bg: 'var(--gray-400)', text: 'var(--black)' }
];

function getOrgColor(orgId: string) {
  let hash = 0;
  for (const ch of orgId) {
    hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  }
  return ORG_AVATAR_COLORS[Math.abs(hash) % ORG_AVATAR_COLORS.length];
}

const SidebarOrgSwitcher: React.FC = () => {
  const {
    organizations,
    currentOrganization,
    isLoading,
    switchingToOrgName,
    handleSwitch,
  } = useOrganizationSwitch();
  const { user } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Apply BRAND_FILTER: restrict the user's visible WorkOS orgs to those listed
  // in the current org's allowed_workos_org_ids. Off when the feature is disabled
  // or the list is empty (defensive) — in which case all orgs are shown.
  const effectiveOrganizations = useMemo(() => {
    const allowedIds = getBrandFilterAllowedIds(user?.features);
    if (!allowedIds) return organizations;
    const allowed = new Set(allowedIds);
    return organizations.filter(
      (org) => org.workosOrganizationId !== undefined && allowed.has(org.workosOrganizationId),
    );
  }, [organizations, user?.features]);

  const hasMultipleOrgs = effectiveOrganizations.length > 1;

  const filteredOrganizations = useMemo(() => {
    if (!searchQuery.trim()) return effectiveOrganizations;
    const q = searchQuery.toLowerCase().trim();
    return effectiveOrganizations.filter((org) =>
      org.name.toLowerCase().includes(q),
    );
  }, [effectiveOrganizations, searchQuery]);

  // Position menu when opening
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const menuWidth = 280;
    const gap = 8;

    const left = triggerRect.right + gap;
    const bottom = viewportHeight - triggerRect.bottom;

    setMenuStyle({
      position: 'fixed',
      left,
      bottom: Math.max(bottom, gap),
      width: menuWidth,
      maxHeight: viewportHeight - gap * 2,
    });
  }, [isOpen]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Clear search on close
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Auto-focus search and scroll selected into view on open
  useEffect(() => {
    if (!isOpen) return;
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
      const selected = listRef.current?.querySelector('[aria-current="true"]');
      selected?.scrollIntoView({ block: 'nearest' });
    });
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const handleOrgClick = useCallback(
    async (orgId: string, orgName: string) => {
      setIsOpen(false);
      await handleSwitch(orgId, orgName);
    },
    [handleSwitch],
  );

  if (organizations.length === 0) {
    return null;
  }

  const currentOrgName = currentOrganization?.name ?? 'Organization';
  const currentColor = currentOrganization
    ? getOrgColor(currentOrganization.id)
    : ORG_AVATAR_COLORS[0];
  const currentInitials = getOrgInitials(currentOrgName);

  return (
    <>
      <div className={styles.orgSwitcherSection}>
        <button
          ref={triggerRef}
          type="button"
          className={classNames(styles.orgTrigger, {
            [styles.active]: isOpen,
          })}
          onClick={() => hasMultipleOrgs && setIsOpen((v) => !v)}
          disabled={isLoading}
          aria-label={`Switch organization. Current: ${currentOrgName}`}
          aria-expanded={hasMultipleOrgs ? isOpen : undefined}
          aria-haspopup={hasMultipleOrgs ? 'menu' : undefined}
          style={{ cursor: hasMultipleOrgs ? 'pointer' : 'default' }}
        >
          <div
            className={styles.orgAvatar}
            style={{ backgroundColor: currentColor.bg, color: currentColor.text }}
          >
            {currentInitials}
          </div>
        </button>
      </div>

      {isOpen &&
        createPortal(
          <>
            <div
              className={styles.orgMenuBackdrop}
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
            <div
              ref={menuRef}
              className={styles.orgMenu}
              style={menuStyle}
              role="menu"
              aria-label="Organization list"
            >
              <div className={styles.orgMenuHeader}>Select Partner</div>
              <div className={styles.orgList} ref={listRef}>
                {filteredOrganizations.map((org) => {
                  const isSelected = org.id === currentOrganization?.id;
                  const color = getOrgColor(org.id);
                  const initials = getOrgInitials(org.name);

                  return (
                    <button
                      key={org.id}
                      type="button"
                      className={classNames(styles.orgMenuItem, {
                        [styles.selected]: isSelected,
                      })}
                      role="menuitem"
                      aria-current={isSelected ? 'true' : undefined}
                      onClick={() => handleOrgClick(org.id, org.name)}
                    >
                      <div
                        className={styles.orgItemAvatar}
                        style={{ backgroundColor: color.bg, color: color.text }}
                      >
                        {initials}
                      </div>
                      <span className={styles.orgItemName}>{org.name}</span>
                      {isSelected && <Check size={16} className={styles.checkIcon} />}
                    </button>
                  );
                })}
                {filteredOrganizations.length === 0 && (
                  <div className={styles.orgListEmpty}>No matches</div>
                )}
              </div>
              <div className={styles.orgMenuSearch}>
                <Search size={14} className={styles.searchIcon} />
                <input
                  ref={searchInputRef}
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </>,
          document.body,
        )}

      {isLoading &&
        createPortal(
          <div className={styles.switchingOverlay}>
            <div className={styles.switchingMessage}>
              <Loader2 className={styles.spinner} size={32} />
              <p className={styles.switchingText}>
                Switching to <strong>{switchingToOrgName}</strong> organization
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SidebarOrgSwitcher;
